import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import * as api from '@gitbook/api-client';
import { webConfig } from '@gitbook/env';
import {
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldError,
    FieldGroup,
    Form,
    Heading,
    Label,
    Submit,
    TextField,
    TextInput,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { apiClient, apiQuery, apiQueryClient } from '../../../../state';
import type { SiteOnlyCustomizationProps } from '../../types';
import { FileUploadControls } from './FileUploadControls';
import type { FontFormData } from './types';

/**
 * Dialog to modify a custom font family.
 */
export function ModifyCustomFontDialog(props: {
    organizationId: string;
    fontId: string;
    setSettings: SiteOnlyCustomizationProps['setSettings'];
}): React.ReactElement {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Modify custom font</Heading>
                <Description>
                    The font will be updated across all sites in your organization.
                </Description>
            </DialogHeader>
            <React.Suspense fallback={null}>
                <ModifyCustomFontForm {...props} />
            </React.Suspense>
        </Dialog>
    );
}

/**
 * Form to create a custom font family.
 */
function ModifyCustomFontForm(props: {
    organizationId: string;
    fontId: string;
    setSettings: SiteOnlyCustomizationProps['setSettings'];
}): React.ReactElement {
    const { organizationId, fontId, setSettings } = props;
    const [isPending, startDialogAction] = useDialogAction();

    const { data: font } = useSuspenseQuery(apiQuery('getCustomFont', { organizationId, fontId }));

    const form = useForm<FontFormData>({
        defaultValues: {
            fontFamily: font.fontFamily,
            fontSources: font.fontFaces
                .map((face) => {
                    const source = face.sources[0];

                    if (!source?.url) {
                        return null;
                    }

                    return {
                        weight: face.weight,
                        // when a font comes from the API, we are given its full URL so need to extract the storage key
                        storageKey: extractStorageKeyFromURL(source.url),
                    };
                })
                .filter(filterOutNullable),
        },
        mode: 'onChange',
    });

    const onSubmit: SubmitHandler<FontFormData> = async (data) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                const { data: updated } = await apiClient.customFonts.updateCustomFont(
                    organizationId,
                    font.id,
                    {
                        fontFamily: data.fontFamily,
                        fontFaces: data.fontSources.map((fontFile) => ({
                            weight: fontFile.weight,
                            storageKey: fontFile.storageKey,
                        })),
                    }
                );

                // We don't have subscriptions for custom fonts yet, just remove the queries from the API cache.
                apiQueryClient.resetQueries({
                    queryKey: apiQuery('getCustomFont', { organizationId, fontId }).queryKey,
                });

                // update the UI instantly
                setSettings((prev) => ({
                    ...prev,
                    styling: {
                        ...prev.styling,
                        font: updated,
                    },
                }));

                toast.success('Custom font updated successfully');
                await close();
            }, 'Error while updating custom font');
        });
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <div className="flex flex-col gap-4">
                    <FieldGroup>
                        <Label>Font family</Label>
                        <Description>Give your custom font a name</Description>
                        <TextField
                            name="fontFamily"
                            required
                            minLength={1}
                            maxLength={api.FONT_FAMILY_MAX_LENGTH}
                        >
                            <TextInput
                                placeholder="e.g. Montserrat, Open Sans, etc."
                                autoFocus
                                disabled={isPending}
                            />
                            <FieldError />
                        </TextField>
                    </FieldGroup>
                    <FileUploadControls organizationId={organizationId} form={form} />
                </div>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Update" disabled={!form.formState.isValid} />
            </DialogFooter>
        </Form>
    );
}

function extractStorageKeyFromURL(url: string): string {
    return url.replace(`https://${webConfig.services.fonts.host}/font/custom/`, '');
}
